import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import "../sass/pages/finance.scss";

/* ==================== Config ==================== */
const API_COBRANCA_BASE_URL = "https://api-cobranca-production-f162.up.railway.app";

/* ==================== Helpers ==================== */
const useDebounce = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtDateOnly = (value) => {
  if (!value) return "-";

  const text = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-");
    return `${day}/${month}/${year}`;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
    return text;
  }

  try {
    return new Date(value).toLocaleDateString("pt-BR");
  } catch {
    return text;
  }
};

const fmtDatePayload = (value) => {
  if (!value) return "";

  const text = String(value);

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return text;

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-");
    return `${day}/${month}/${year}`;
  }

  return fmtDateOnly(value);
};

const fmtDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return String(value);
  }
};

const money = (value) =>
  Number(value ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const getFirstFilled = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};

const getNested = (obj, path) => {
  try {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  } catch {
    return undefined;
  }
};

const normalizarClienteApi = (response) => {
  if (!response) return null;

  if (Array.isArray(response)) return response[0] || null;

  if (response.cliente) return response.cliente;

  if (response.data && Array.isArray(response.data)) return response.data[0] || null;

  if (response.data && typeof response.data === "object") return response.data;

  return response;
};

const obterTelefoneDeObjeto = (obj = {}) => {
  return getFirstFilled(
    obj.whatsapp,
    obj.WhatsApp,
    obj.celular,
    obj.Celular,
    obj.Cel,
    obj.cel,
    obj.cel1,
    obj.Cel1,
    obj.cel2,
    obj.Cel2,
    obj.telefone,
    obj.Telefone,
    obj.fone,
    obj.Fone,
    obj.tel1,
    obj.Tel1,
    obj.tel2,
    obj.Tel2,
    obj.TelContador,
    obj.contatoTelefone,
    obj.ContatoTelefone,
    getNested(obj, "cliente.Cel"),
    getNested(obj, "cliente.Tel1"),
    getNested(obj, "cliente.Tel2"),
    getNested(obj, "cliente.TelContador")
  );
};

const obterTelefoneCobranca = (detalhe = {}, clienteComplementar = null) => {
  return getFirstFilled(
    obterTelefoneDeObjeto(detalhe),
    obterTelefoneDeObjeto(clienteComplementar || {})
  );
};

const obterLinkSegundaVia = (detalhe = {}) => {
  return getFirstFilled(
    detalhe.linkSegundaVia,
    detalhe.LinkSegundaVia,
    detalhe.linkBoleto,
    detalhe.LinkBoleto,
    detalhe.urlBoleto,
    detalhe.UrlBoleto,
    detalhe.segundaVia,
    detalhe.SegundaVia,
    detalhe.linkPagamento,
    detalhe.LinkPagamento,
    detalhe.urlPagamento,
    detalhe.UrlPagamento
  );
};

const obterClienteId = (detalhe = {}) => {
  return getFirstFilled(
    detalhe.clienteId,
    detalhe.idCliente,
    detalhe.id_cliente,
    detalhe.codCli,
    detalhe.CodCli,
    detalhe.CodigoCad,
    detalhe.codigoCad
  );
};

const obterNomeCliente = (detalhe = {}, clienteComplementar = null) => {
  return getFirstFilled(
    detalhe.cliente,
    detalhe.nomeCliente,
    detalhe.NomeCliente,
    detalhe.razaoSocial,
    detalhe.RazaoSocial,
    detalhe.NomeFant,
    clienteComplementar?.RazaoSocial,
    clienteComplementar?.NomeFant,
    clienteComplementar?.cliente?.RazaoSocial,
    clienteComplementar?.cliente?.NomeFant
  );
};


const gerarLinkPortalCobranca = async ({
  nrLicenca,
  idEmpresa,
  idCliente,
}) => {
  if (!idCliente) {
    throw new Error("Código do cliente não informado para gerar o portal financeiro.");
  }

  const response = await fetch(`${API_COBRANCA_BASE_URL}/portal-cobranca/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-licenca": String(nrLicenca || ""),
      "x-id-empresa": String(idEmpresa || 1),
    },
    body: JSON.stringify({
      nrLicenca: String(nrLicenca || ""),
      idEmpresa: Number(idEmpresa || 1),
      idCliente: Number(idCliente),
      origem: "COBRANCA_INTERNA",
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data?.sucesso || !data?.dados?.url) {
    throw new Error(
      data?.mensagem ||
        data?.erro ||
        "Falha ao gerar link do portal financeiro."
    );
  }

  return data.dados.url;
};

/* ==================== Sort ==================== */
const ORDER_OPTS = [
  { v: "vencimento", l: "Vencimento" },
  { v: "diasAtraso", l: "Dias atraso" },
  { v: "valor", l: "Valor" },
  { v: "cliente", l: "Cliente" },
  { v: "numTitulo", l: "Nº título" },
  { v: "parcela", l: "Parcela" },
  { v: "situacao", l: "Situação" },
  { v: "statusCob", l: "Status cobrança" },
];

function SortHeader({ label, field, orderBy, dir, onSort, style }) {
  const active = orderBy === field;
  const nextDir = active && dir === "asc" ? "desc" : "asc";

  return (
    <th
      scope="col"
      style={style}
      className={`${active ? `is-active ${dir}` : ""} sortable`}
      onClick={() => onSort(field, nextDir)}
      title={`Ordenar por ${label} (${nextDir})`}
    >
      <div className="th-sort">
        <span>{label}</span>
        <span className="sort-ind">{active ? (dir === "asc" ? "▲" : "▼") : "⇅"}</span>
      </div>
    </th>
  );
}

/* ==================== Drawer ==================== */
function Drawer({ open, title, onClose, children }) {
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (event) => event.key === "Escape" && onClose?.();

    window.addEventListener("keydown", onKey);
    setTimeout(() => firstRef.current?.focus(), 50);

    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="drawer" role="dialog" aria-modal onClick={onClose}>
      <div className="drawer-body" onClick={(event) => event.stopPropagation()}>
        <div className="drawer-header">
          <h2>{title}</h2>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>

        {typeof children === "function" ? children({ firstRef }) : children}
      </div>
    </div>
  );
}

/* ==================== Página ==================== */
export default function FinanceiroRecebiveis() {
  const [filtro, setFiltro] = useState("atrasados");
  const [q, setQ] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [vencDe, setVencDe] = useState("");
  const [vencAte, setVencAte] = useState("");
  const [orderBy, setOrderBy] = useState("vencimento");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const qDeb = useDebounce(q, 250);

  const [kpi, setKpi] = useState({
    emAtraso: 0,
    atrasoMedioDias: 0,
    hojeFollowups: 0,
    pagosNoMes: 0,
  });

  const [det, setDet] = useState(null);
  const [clienteComplementar, setClienteComplementar] = useState(null);
  const [loadingClienteComplementar, setLoadingClienteComplementar] = useState(false);
  const [tab, setTab] = useState("overview");

  const [postLoading, setPostLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [envioWhatsappLoading, setEnvioWhatsappLoading] = useState(false);

  const [form, setForm] = useState({
    canal: "fone",
    resultado: "SEM_CONTATO",
    promessaValor: "",
    promessaData: "",
    formaPagto: "",
    proximaAcaoData: "",
    proximaAcaoDesc: "",
    proximaAcaoResponsavelId: "",
    observacao: "",
  });

  const load = async (_page = page, _orderBy = orderBy, _dir = dir) => {
    setLoading(true);

    try {
      const { data } = await api.get("/finance/recebiveis", {
        params: {
          filtro,
          search: qDeb || undefined,
          clienteId: clienteId || undefined,
          vencDe: vencDe || undefined,
          vencAte: vencAte || undefined,
          page: _page,
          limit,
          orderBy: _orderBy,
          dir: _dir,
        },
      });

      setRows(data?.data || []);
      setTotal(Number(data?.total || 0));
      setPage(Number(data?.page || _page));
    } catch (error) {
      showError(error?.response?.data?.message || "Falha ao carregar recebíveis.");
    } finally {
      setLoading(false);
    }
  };

  const loadKpis = async () => {
    try {
      const { data } = await api.get("/finance/kpis", {
        params: { refDate: todayISO() },
      });

      setKpi(data || {});
    } catch {
      // Mantém operação mesmo se KPI falhar.
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, qDeb, clienteId, vencDe, vencAte, limit, orderBy, dir]);

  useEffect(() => {
    loadKpis();
  }, []);

  const buscarClienteComplementar = async (codigoCliente) => {
    const codigo = String(codigoCliente || "").trim();

    if (!codigo) return null;

    setLoadingClienteComplementar(true);

    try {
      const { data } = await api.get("/ssw/cliente/por-codigo", {
        params: {
          codigo,
          incluirDesativados: true,
        },
      });

      const cliente = normalizarClienteApi(data);
      setClienteComplementar(cliente);

      return cliente;
    } catch (error) {
      console.warn("[FINANCEIRO][CLIENTE_COMPLEMENTAR][ERRO]", error);
      setClienteComplementar(null);
      return null;
    } finally {
      setLoadingClienteComplementar(false);
    }
  };

  const openDet = async (tituloId) => {
    try {
      setClienteComplementar(null);

      const { data } = await api.get(`/finance/recebiveis/${tituloId}`);
      setDet(data || null);
      setTab("overview");

      const detalhe = data?.detalhe || {};
      const codigoCliente = obterClienteId(detalhe);

      if (codigoCliente) {
        await buscarClienteComplementar(codigoCliente);
      }
    } catch (error) {
      showError(error?.response?.data?.message || "Falha ao carregar detalhe.");
    }
  };

  const resetForm = () =>
    setForm({
      canal: "fone",
      resultado: "SEM_CONTATO",
      promessaValor: "",
      promessaData: "",
      formaPagto: "",
      proximaAcaoData: "",
      proximaAcaoDesc: "",
      proximaAcaoResponsavelId: "",
      observacao: "",
    });

  const postInteracao = async () => {
    if (!det?.detalhe?.tituloId) return;

    setPostLoading(true);

    try {
      const payload = {
        tituloId: det.detalhe.tituloId,
        canal: form.canal,
        resultado: form.resultado,
        promessaValor: form.promessaValor ? Number(form.promessaValor) : undefined,
        promessaData: form.promessaData || undefined,
        formaPagto: form.formaPagto || undefined,
        proximaAcaoData: form.proximaAcaoData || undefined,
        proximaAcaoDesc: form.proximaAcaoDesc || undefined,
        proximaAcaoResponsavelId: form.proximaAcaoResponsavelId
          ? Number(form.proximaAcaoResponsavelId)
          : undefined,
        observacao: form.observacao || undefined,
      };

      await api.post("/finance/cobrancas", payload);

      showInfo("Interação registrada.");

      const { data: fresh } = await api.get(`/finance/recebiveis/${det.detalhe.tituloId}`);
      setDet(fresh || det);
      resetForm();
      loadKpis();
    } catch (error) {
      showError(error?.response?.data?.message || "Falha ao criar interação.");
    } finally {
      setPostLoading(false);
    }
  };

  const updateStatus = async (statusCob) => {
    if (!det?.detalhe?.tituloId) return;

    setUpdatingStatus(true);

    try {
      await api.patch(`/finance/recebiveis/${det.detalhe.tituloId}/status`, {
        statusCob,
      });

      showInfo("Status atualizado.");

      const { data } = await api.get(`/finance/recebiveis/${det.detalhe.tituloId}`);

      setDet(data || det);
      load();
    } catch (error) {
      showError(error?.response?.data?.message || "Falha ao atualizar status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const registrarInteracaoWhatsapp = async ({ detalhe, logId, twilioSid, linkPortalFinanceiro }) => {
    try {
      await api.post("/finance/cobrancas", {
        tituloId: detalhe.tituloId,
        canal: "whatsapp",
        resultado: "COBRANCA_ENVIADA",
        observacao: [
          "Cobrança enviada via WhatsApp Business.",
          logId ? `Log API Cobrança: ${logId}.` : null,
          twilioSid ? `Twilio SID: ${twilioSid}.` : null,
          linkPortalFinanceiro ? `Portal financeiro: ${linkPortalFinanceiro}.` : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
    } catch (error) {
      console.warn("[FINANCEIRO][WHATSAPP][TIMELINE][ERRO]", error);
    }
  };

  const enviarCobrancaWhatsapp = async () => {
    if (!det?.detalhe) return;

    const detalhe = det.detalhe;
    let cliente = clienteComplementar;

    const codigoCliente = obterClienteId(detalhe);

    if (!cliente && codigoCliente) {
      cliente = await buscarClienteComplementar(codigoCliente);
    }

    const telefone = obterTelefoneCobranca(detalhe, cliente);
    const telefoneNormalizado = onlyDigits(telefone);

    if (!telefoneNormalizado || telefoneNormalizado.length < 10) {
      showError("Cliente sem telefone/WhatsApp válido cadastrado.");
      return;
    }

    const nomeCliente = obterNomeCliente(detalhe, cliente);

    const mensagemPreview =
      `Confirmar envio de cobrança por WhatsApp?\n\n` +
      `Cliente: ${nomeCliente}\n` +
      `Código: ${codigoCliente || "-"}\n` +
      `Telefone: ${telefoneNormalizado}\n` +
      `Título: ${detalhe.numTitulo || detalhe.tituloId}\n` +
      `Parcela: ${detalhe.parcela || "-"}\n` +
      `Vencimento: ${fmtDateOnly(detalhe.vencimento)}\n` +
      `Valor: ${money(detalhe.valor)}\n` +
      `Portal financeiro: será gerado automaticamente\n\n` +
      "O envio será feito pela API oficial de cobrança.";

    const confirmar = window.confirm(mensagemPreview);

    if (!confirmar) return;

    setEnvioWhatsappLoading(true);

    try {
      const nrLicenca =
        detalhe.nrLicenca ||
        detalhe.nr_licenca ||
        detalhe.licenca ||
        codigoCliente ||
        window.sessionStorage.getItem("nrLicenca") ||
        window.localStorage.getItem("nrLicenca") ||
        "";

      const idEmpresa =
        detalhe.idEmpresa ||
        detalhe.id_empresa ||
        window.sessionStorage.getItem("idEmpresa") ||
        window.localStorage.getItem("idEmpresa") ||
        1;

      const idClientePortal = codigoCliente || nrLicenca;

      const linkPortalFinanceiro = await gerarLinkPortalCobranca({
        nrLicenca: String(nrLicenca || ""),
        idEmpresa: Number(idEmpresa || 1),
        idCliente: idClientePortal,
      });

      const payload = {
        nrLicenca: String(nrLicenca || ""),
        idEmpresa: Number(idEmpresa || 1),
        idCliente: idClientePortal ? Number(idClientePortal) : null,
        idTitulo: detalhe.tituloId,
        telefone: telefoneNormalizado,
        nomeCliente,
        valor: detalhe.valor,
        vencimento: fmtDatePayload(detalhe.vencimento),
        linkSegundaVia: linkPortalFinanceiro,
      };

      const response = await fetch(`${API_COBRANCA_BASE_URL}/whatsapp-twilio/cobranca`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-licenca": String(payload.nrLicenca || ""),
          "x-id-empresa": String(payload.idEmpresa || ""),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.sucesso) {
        throw new Error(
          data?.mensagem ||
            data?.erro ||
            "Falha ao enviar cobrança pela API de cobrança."
        );
      }

      const logId = data?.dados?.logId || null;
      const twilioSid = data?.dados?.response?.sid || null;

      await registrarInteracaoWhatsapp({
        detalhe,
        logId,
        twilioSid,
        linkPortalFinanceiro,
      });

      showInfo("Cobrança WhatsApp enviada com sucesso.");

      const { data: fresh } = await api.get(`/finance/recebiveis/${detalhe.tituloId}`);
      setDet(fresh || det);

      load();
      loadKpis();
    } catch (error) {
      showError(error?.message || "Falha ao enviar cobrança por WhatsApp.");
    } finally {
      setEnvioWhatsappLoading(false);
    }
  };

  const pages = useMemo(
    () => Math.max(1, Math.ceil(total / Math.max(1, limit))),
    [total, limit]
  );

  const startIdx = total > 0 ? (page - 1) * limit + 1 : 0;
  const endIdx = Math.min(total, page * limit);

  const onSort = (field, nextDir) => {
    setOrderBy(field);
    setDir(nextDir);
    load(1, field, nextDir);
  };

  const detalhe = det?.detalhe || {};
  const telefoneDetalhe = obterTelefoneCobranca(detalhe, clienteComplementar);
  const telefoneDetalheNormalizado = onlyDigits(telefoneDetalhe);
  const linkSegundaViaDetalhe = obterLinkSegundaVia(detalhe);
  const clienteIdDetalhe = obterClienteId(detalhe);
  const portalFinanceiroDisponivel = Boolean(clienteIdDetalhe);
  const nomeClienteDetalhe = obterNomeCliente(detalhe, clienteComplementar);

  return (
    <MainLayout>
      <div className="finance-page">
        <div className="proj-toolbar">
          <div className="left">
            <h1>Recebíveis</h1>
            <div className="hint">
              Total: <b>{total}</b> títulos • {startIdx}-{endIdx}
            </div>
          </div>

          <div className="right">
            <Button onClick={() => load(page)} disabled={loading}>
              Atualizar
            </Button>
          </div>
        </div>

        <div className="kpi-grid">
          <div className="kpi">
            <div className="kpi-label">Em atraso (R$)</div>
            <div className="kpi-value">{money(kpi.emAtraso)}</div>
          </div>

          <div className="kpi">
            <div className="kpi-label">Atraso médio (dias)</div>
            <div className="kpi-value">
              {Number(kpi.atrasoMedioDias || 0).toFixed(0)}
            </div>
          </div>

          <div className="kpi">
            <div className="kpi-label">Follow-ups hoje</div>
            <div className="kpi-value">{kpi.hojeFollowups || 0}</div>
          </div>

          <div className="kpi">
            <div className="kpi-label">Pagos no mês (R$)</div>
            <div className="kpi-value">{money(kpi.pagosNoMes)}</div>
          </div>
        </div>

        <div className="filters-bar">
          <div className="filters-scroll">
            <select
              className="input native"
              value={filtro}
              onChange={(event) => setFiltro(event.target.value)}
            >
              <option value="atrasados">Atrasados</option>
              <option value="pagos">Pagos</option>
              <option value="todos">Todos</option>
            </select>

            <Input
              placeholder="Buscar cliente / CNPJ / cód / Nº título"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              style={{ minWidth: 260 }}
            />

            <Input
              placeholder="Cliente ID"
              value={clienteId}
              onChange={(event) => setClienteId(event.target.value)}
              style={{ width: 120 }}
            />

            <div className="inline-field">
              <Label>Venc. de</Label>
              <Input
                type="date"
                value={vencDe}
                onChange={(event) => setVencDe(event.target.value)}
              />
            </div>

            <div className="inline-field">
              <Label>Venc. até</Label>
              <Input
                type="date"
                value={vencAte}
                onChange={(event) => setVencAte(event.target.value)}
              />
            </div>

            <select
              className="input native"
              value={orderBy}
              onChange={(event) => setOrderBy(event.target.value)}
            >
              {ORDER_OPTS.map((option) => (
                <option key={option.v} value={option.v}>
                  {option.l}
                </option>
              ))}
            </select>

            <select
              className="input native"
              value={dir}
              onChange={(event) => setDir(event.target.value)}
            >
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>

            <select
              className="input native"
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
            >
              {[10, 20, 50, 100].map((value) => (
                <option key={value} value={value}>
                  Limite: {value}
                </option>
              ))}
            </select>

            <Button
              variant="secondary"
              onClick={() => {
                setVencDe("");
                setVencAte("");
                setQ("");
                setClienteId("");
                setFiltro("atrasados");
              }}
            >
              Limpar
            </Button>
          </div>
        </div>

        <div className="card">
          <div className="table-wrap">
            <table className="table-sm w-full">
              <thead className="sticky">
                <tr>
                  <SortHeader
                    label="#"
                    field="rownum"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                    style={{ width: 90 }}
                  />

                  <SortHeader
                    label="Cliente"
                    field="cliente"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                    style={{ minWidth: 220 }}
                  />

                  <SortHeader
                    label="Nº título"
                    field="numTitulo"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                  />

                  <SortHeader
                    label="Parcela"
                    field="parcela"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                  />

                  <SortHeader
                    label="Vencimento"
                    field="vencimento"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                  />

                  <SortHeader
                    label="Dias atraso"
                    field="diasAtraso"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                    style={{ width: 110 }}
                  />

                  <SortHeader
                    label="Valor"
                    field="valor"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                    style={{ width: 120 }}
                  />

                  <SortHeader
                    label="Situação"
                    field="situacao"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                    style={{ minWidth: 140 }}
                  />

                  <SortHeader
                    label="Status cobrança"
                    field="statusCob"
                    orderBy={orderBy}
                    dir={dir}
                    onSort={onSort}
                    style={{ minWidth: 160 }}
                  />

                  <th
                    style={{
                      minWidth: 200,
                      position: "sticky",
                      right: 0,
                      background: "#fff",
                      boxShadow: "inset 1px 0 0 #eee",
                    }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <tr key={index}>
                      <td colSpan={10}>
                        <div className="skeleton-line" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10}>
                      <div className="empty-sub">Sem dados.</div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr
                      key={row.tituloId}
                      className={
                        row.diasAtraso > 0 && row.situacao === "P"
                          ? "row-overdue"
                          : ""
                      }
                    >
                      <td className="mono">{(page - 1) * limit + index + 1}</td>

                      <td
                        className="truncate"
                        title={`#${row.clienteId || row.codCli || ""} — ${row.cnpj || ""}`}
                      >
                        {row.cliente}
                      </td>

                      <td className="mono">{row.numTitulo}</td>
                      <td className="mono">{row.parcela}</td>
                      <td>{fmtDateOnly(row.vencimento)}</td>
                      <td className="mono">{row.diasAtraso}</td>
                      <td className="mono">{money(row.valor)}</td>

                      <td>
                        {row.situacao === "Q" ? (
                          <span className="badge badge-green">Quitado</span>
                        ) : (
                          <span className="badge badge-amber">Pendente</span>
                        )}
                      </td>

                      <td>
                        {row.statusCob ? (
                          <span className="badge badge-blue">{row.statusCob}</span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td
                        style={{
                          position: "sticky",
                          right: 0,
                          background: "#fff",
                          boxShadow: "inset 1px 0 0 #eee",
                        }}
                      >
                        <div className="row-actions">
                          <Button variant="secondary" onClick={() => openDet(row.tituloId)}>
                            Detalhe
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <Button
              variant="secondary"
              onClick={() => {
                setPage(1);
                load(1);
              }}
              disabled={page <= 1}
            >
              « Primeiro
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                const previousPage = Math.max(1, page - 1);
                setPage(previousPage);
                load(previousPage);
              }}
              disabled={page <= 1}
            >
              ‹ Anterior
            </Button>

            <span className="mono">
              pág. {page} / {pages}
            </span>

            <Button
              variant="secondary"
              onClick={() => {
                const nextPage = Math.min(pages, page + 1);
                setPage(nextPage);
                load(nextPage);
              }}
              disabled={page >= pages}
            >
              Próxima ›
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                setPage(pages);
                load(pages);
              }}
              disabled={page >= pages}
            >
              Última »
            </Button>
          </div>
        </div>

        <Drawer
          open={!!det}
          title={
            det
              ? `Título #${detalhe.numTitulo} • Cliente ${nomeClienteDetalhe}`
              : ""
          }
          onClose={() => setDet(null)}
        >
          {({ firstRef }) =>
            det && (
              <>
                <div className="tabs">
                  <button
                    className={`tab ${tab === "overview" ? "active" : ""}`}
                    onClick={() => setTab("overview")}
                  >
                    Resumo
                  </button>

                  <button
                    className={`tab ${tab === "timeline" ? "active" : ""}`}
                    onClick={() => setTab("timeline")}
                  >
                    Timeline
                  </button>

                  <button
                    className={`tab ${tab === "nova" ? "active" : ""}`}
                    onClick={() => setTab("nova")}
                  >
                    Nova interação
                  </button>
                </div>

                {tab === "overview" && (
                  <>
                    <div className="grid grid-3">
                      <div>
                        <b>Cliente:</b> {nomeClienteDetalhe}{" "}
                        <small className="mono">#{clienteIdDetalhe || "-"}</small>
                      </div>

                      <div>
                        <b>CNPJ:</b>{" "}
                        {detalhe.cnpj ||
                          detalhe.CpfCnpj ||
                          clienteComplementar?.CpfCnpjLimpo ||
                          clienteComplementar?.CpfCnpj ||
                          "-"}
                      </div>

                      <div>
                        <b>Nº título:</b> {detalhe.numTitulo} — Parcela{" "}
                        {detalhe.parcela}
                      </div>

                      <div>
                        <b>Vencimento:</b> {fmtDateOnly(detalhe.vencimento)}
                      </div>

                      <div>
                        <b>Valor:</b> {money(detalhe.valor)}
                      </div>

                      <div>
                        <b>Situação:</b>{" "}
                        {detalhe.situacao === "Q" ? "Quitado" : "Pendente"}
                      </div>

                      <div>
                        <b>Status cobrança:</b> {detalhe.statusCob || "-"}
                      </div>

                      <div>
                        <b>Próx. ação:</b>{" "}
                        {detalhe.proximaAcaoData
                          ? fmtDateTime(detalhe.proximaAcaoData)
                          : "-"}
                      </div>

                      <div>
                        <b>WhatsApp:</b>{" "}
                        {loadingClienteComplementar
                          ? "buscando..."
                          : telefoneDetalheNormalizado || "-"}
                      </div>

                      <div className="col-span-3">
                        <b>Portal financeiro:</b>{" "}
                        {linkSegundaViaDetalhe ? (
                          <a
                            href={linkSegundaViaDetalhe}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Abrir link atual
                          </a>
                        ) : portalFinanceiroDisponivel ? (
                          "Será gerado automaticamente no envio do WhatsApp."
                        ) : (
                          "Cliente sem código para geração do portal."
                        )}
                      </div>
                    </div>

                    <div className="divider" />

                    <div className="card soft whatsapp-action-card">
                      <div style={{ fontWeight: 800, marginBottom: 8 }}>
                        Cobrança por WhatsApp Business
                      </div>

                      <div className="muted" style={{ marginBottom: 10 }}>
                        Envio manual via template aprovado. O telefone é enriquecido
                        pela rota de cliente quando não vier no detalhe financeiro.
                      </div>

                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <Button
                          variant="secondary"
                          onClick={enviarCobrancaWhatsapp}
                          disabled={
                            envioWhatsappLoading ||
                            loadingClienteComplementar ||
                            !telefoneDetalheNormalizado ||
                            !portalFinanceiroDisponivel
                          }
                        >
                          {envioWhatsappLoading
                            ? "Enviando WhatsApp…"
                            : "Enviar cobrança WhatsApp"}
                        </Button>

                        {!telefoneDetalheNormalizado ? (
                          <span className="badge badge-amber">
                            Sem telefone cadastrado
                          </span>
                        ) : (
                          <span className="badge badge-green">
                            Telefone: {telefoneDetalheNormalizado}
                          </span>
                        )}

                        {!portalFinanceiroDisponivel ? (
                          <span className="badge badge-amber">
                            Sem código do cliente
                          </span>
                        ) : (
                          <span className="badge badge-blue">
                            Portal será gerado no envio
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {tab === "timeline" && (
                  <div>
                    {det.timeline?.length ? (
                      <ul className="timeline">
                        {det.timeline.map((item) => (
                          <li key={item.id} className="tl-item">
                            <div className="tl-head">
                              <b>{item.canal}</b> — {item.resultado}
                              <span className="dot">•</span>
                              <span>{fmtDateTime(item.createdAt)}</span>
                            </div>

                            <div className="tl-body">
                              {item.promessaValor ? (
                                <div>
                                  <b>Promessa:</b> {money(item.promessaValor)}{" "}
                                  {item.promessaData
                                    ? `em ${fmtDateTime(item.promessaData)}`
                                    : ""}
                                </div>
                              ) : null}

                              {item.formaPagto ? (
                                <div>
                                  <b>Forma pagto:</b> {item.formaPagto}
                                </div>
                              ) : null}

                              {item.proximaAcaoData ? (
                                <div>
                                  <b>Próx. ação:</b>{" "}
                                  {fmtDateTime(item.proximaAcaoData)}{" "}
                                  {item.proximaAcaoDesc
                                    ? `— ${item.proximaAcaoDesc}`
                                    : ""}
                                </div>
                              ) : null}

                              {item.observacao ? (
                                <div className="muted">{item.observacao}</div>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="empty-sub">Sem interações.</div>
                    )}
                  </div>
                )}

                {tab === "nova" && (
                  <div className="grid grid-3">
                    <div>
                      <Label>Canal</Label>
                      <select
                        ref={firstRef}
                        className="input native"
                        value={form.canal}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            canal: event.target.value,
                          }))
                        }
                      >
                        <option value="fone">Telefone</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">E-mail</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <Label>Resultado</Label>
                      <select
                        className="input native"
                        value={form.resultado}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            resultado: event.target.value,
                          }))
                        }
                      >
                        <option value="SEM_CONTATO">Sem contato</option>
                        <option value="NEGOCIANDO">Negociando</option>
                        <option value="PROMETIDO">Prometido</option>
                        <option value="RECUSOU">Recusou</option>
                        <option value="CONTESTOU">Contestou</option>
                      </select>
                    </div>

                    <div>
                      <Label>Forma de pagamento</Label>
                      <Input
                        value={form.formaPagto}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            formaPagto: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label>Promessa (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.promessaValor}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            promessaValor: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label>Data da promessa</Label>
                      <Input
                        type="datetime-local"
                        value={form.promessaData}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            promessaData: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="col-span-3">
                      <Label>Observação</Label>
                      <textarea
                        className="input native"
                        rows={4}
                        value={form.observacao}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            observacao: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label>Próxima ação — quando</Label>
                      <Input
                        type="datetime-local"
                        value={form.proximaAcaoData}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            proximaAcaoData: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label>Próxima ação — descrição</Label>
                      <Input
                        value={form.proximaAcaoDesc}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            proximaAcaoDesc: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label>Resp. (ID)</Label>
                      <Input
                        value={form.proximaAcaoResponsavelId}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            proximaAcaoResponsavelId: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="col-span-3 flex justify-end" style={{ gap: 8 }}>
                      <Button variant="secondary" onClick={resetForm}>
                        Limpar
                      </Button>

                      <Button onClick={postInteracao} disabled={postLoading}>
                        {postLoading ? "Enviando…" : "Salvar interação"}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="divider" />

                <div className="card soft">
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>
                    Status da Cobrança
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["EM_COBRANCA", "PROMETIDO", "PARCIAL", "PAGO"].map((status) => (
                      <Button
                        key={status}
                        variant="secondary"
                        onClick={() => updateStatus(status)}
                        disabled={updatingStatus}
                      >
                        {status}
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )
          }
        </Drawer>
      </div>
    </MainLayout>
  );
}
